import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from "../../lib/utils";
import { IconLayoutNavbarCollapse } from "@tabler/icons-react";
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform, } from "framer-motion";
import { useRef, useState } from "react";
/**
 * Floating Dock Component (Layer 1 Primitive)
 *
 * A macOS-style floating dock with icon magnification on hover.
 * Demonstrates advanced framer-motion techniques:
 *
 * Advanced Framer Motion Techniques:
 * - useMotionValue: Mouse tracking for distance calculations
 * - useTransform: Transform mouse position to icon size
 * - useSpring: Physics-based spring animations (mass, stiffness, damping)
 * - AnimatePresence: Smooth enter/exit animations
 * - layoutId: Shared layout animations between states
 * - Staggered animations: Sequential item reveals with delays
 *
 * @example
 * ```tsx
 * <FloatingDock
 *   items={[
 *     { title: "Home", icon: <HomeIcon />, onClick: () => navigate('/') },
 *     { title: "Settings", icon: <SettingsIcon />, onClick: () => navigate('/settings') }
 *   ]}
 *   activeIndex={0}
 *   accentColor="#3B82F6"
 * />
 * ```
 */
export const FloatingDock = ({ items, desktopClassName, mobileClassName, activeIndex, accentColor, }) => {
    return (_jsxs(_Fragment, { children: [_jsx(FloatingDockDesktop, { items: items, className: desktopClassName, activeIndex: activeIndex, accentColor: accentColor }), _jsx(FloatingDockMobile, { items: items, className: mobileClassName, activeIndex: activeIndex, accentColor: accentColor })] }));
};
const FloatingDockMobile = ({ items, className, activeIndex, accentColor, }) => {
    const [open, setOpen] = useState(false);
    return (_jsxs("div", { className: cn("fixed bottom-4 right-4 z-50 block md:hidden", className), children: [_jsx(AnimatePresence, { children: open && (_jsx(motion.div, { layoutId: "nav", className: "absolute inset-x-0 bottom-full mb-2 flex flex-col gap-2", children: items.map((item, idx) => (_jsx(motion.div, { initial: { opacity: 0, y: 10 }, animate: {
                            opacity: 1,
                            y: 0,
                        }, exit: {
                            opacity: 0,
                            y: 10,
                            transition: {
                                delay: idx * 0.05,
                            },
                        }, transition: { delay: (items.length - 1 - idx) * 0.05 }, children: _jsx("button", { onClick: () => {
                                item.onClick();
                                setOpen(false);
                            }, className: cn("flex h-10 w-10 items-center justify-center rounded-full transition-colors", activeIndex === idx
                                ? "ring-2 ring-offset-2"
                                : "bg-gray-50 dark:bg-neutral-900"), style: activeIndex === idx && accentColor
                                ? {
                                    backgroundColor: accentColor,
                                    '--tw-ring-color': accentColor,
                                    '--tw-ring-opacity': '0.5',
                                }
                                : undefined, children: _jsx("div", { className: cn("h-4 w-4", activeIndex === idx && "[&>svg]:text-white"), children: item.icon }) }, item.title) }, item.title))) })) }), _jsx("button", { onClick: () => setOpen(!open), className: "flex h-10 w-10 items-center justify-center rounded-full bg-gray-50 dark:bg-neutral-800", children: _jsx(IconLayoutNavbarCollapse, { className: "h-5 w-5 text-neutral-500 dark:text-neutral-400" }) })] }));
};
const FloatingDockDesktop = ({ items, className, activeIndex, accentColor, }) => {
    let mouseX = useMotionValue(Infinity);
    return (_jsx(motion.div, { onMouseMove: (e) => mouseX.set(e.pageX), onMouseLeave: () => mouseX.set(Infinity), className: cn("fixed bottom-6 left-1/2 -translate-x-1/2 z-50 hidden h-16 items-end gap-4 rounded-2xl bg-gray-50 px-4 pb-3 md:flex dark:bg-neutral-900 shadow-lg", className), children: items.map((item, idx) => (_jsx(IconContainer, { mouseX: mouseX, ...item, isActive: activeIndex === idx, accentColor: accentColor }, item.title))) }));
};
function IconContainer({ mouseX, title, icon, onClick, isActive, accentColor, href, }) {
    let ref = useRef(null);
    let distance = useTransform(mouseX, (val) => {
        let bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 };
        return val - bounds.x - bounds.width / 2;
    });
    let widthTransform = useTransform(distance, [-150, 0, 150], [40, 80, 40]);
    let heightTransform = useTransform(distance, [-150, 0, 150], [40, 80, 40]);
    let widthTransformIcon = useTransform(distance, [-150, 0, 150], [20, 40, 20]);
    let heightTransformIcon = useTransform(distance, [-150, 0, 150], [20, 40, 20]);
    let width = useSpring(widthTransform, {
        mass: 0.1,
        stiffness: 150,
        damping: 12,
    });
    let height = useSpring(heightTransform, {
        mass: 0.1,
        stiffness: 150,
        damping: 12,
    });
    let widthIcon = useSpring(widthTransformIcon, {
        mass: 0.1,
        stiffness: 150,
        damping: 12,
    });
    let heightIcon = useSpring(heightTransformIcon, {
        mass: 0.1,
        stiffness: 150,
        damping: 12,
    });
    const [hovered, setHovered] = useState(false);
    return (_jsx("button", { onClick: onClick, children: _jsxs(motion.div, { ref: ref, style: {
                width,
                height,
                ...(isActive && accentColor
                    ? {
                        backgroundColor: accentColor,
                        '--tw-ring-color': accentColor,
                        '--tw-ring-opacity': '0.5',
                    }
                    : {}),
            }, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), className: cn("relative flex aspect-square items-center justify-center rounded-full transition-colors", isActive
                ? "ring-2 ring-offset-2 ring-offset-gray-50 dark:ring-offset-neutral-900"
                : "bg-gray-200 dark:bg-neutral-800"), children: [_jsx(AnimatePresence, { children: hovered && (_jsx(motion.div, { initial: { opacity: 0, y: 10, x: "-50%" }, animate: { opacity: 1, y: 0, x: "-50%" }, exit: { opacity: 0, y: 2, x: "-50%" }, className: "absolute -top-8 left-1/2 w-fit rounded-md border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs whitespace-pre text-neutral-700 dark:border-neutral-900 dark:bg-neutral-800 dark:text-white", children: title })) }), _jsx(motion.div, { layoutId: href ? `nav-icon-${href}` : undefined, style: { width: widthIcon, height: heightIcon }, transition: { type: "spring", stiffness: 300, damping: 30 }, className: cn("flex items-center justify-center", isActive && "[&>svg]:text-white dark:[&>svg]:text-white"), children: icon })] }) }));
}
//# sourceMappingURL=FloatingDock.js.map